"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import type { IconType } from "react-icons";
import SectionHeader from "@/components/shared/headers/SectionHeader";
import { useDict } from "@/components/providers/LocaleProvider";
import { workflowStepsData } from "@/data/roadmap.data";

/**
 * RoadmapSection — the "See How We Work" section.
 * ----------------------------------------------------------------------
 * A scroll-driven workflow roadmap composed of two independent,
 * decoupled visual layers:
 *
 *   1) PATH LAYER  – ONE continuous decorative dashed SVG that
 *      threads through every step. The path's anchors sit at each
 *      card's CENTER (in the same column as the card), so the
 *      rocket visibly enters one card, passes behind it, and exits
 *      toward the next — the cards are checkpoints on the route,
 *      not bystanders next to it. `aria-hidden`, sits BEHIND the
 *      cards via z-index, never affects layout.
 *
 *   2) CARD LAYER  – the real semantic content. A 2-column grid
 *      where each card spans 2 rows and starts one row down from
 *      the previous one (CSS `.lp-card` rule), producing a tight
 *      alternating staircase: left → right → left → right →
 *      left, with consecutive cards overlapping vertically in
 *      adjacent columns so the path bends between them naturally.
 *
 * Each card displays the same illustration + step pill + title +
 * description that the original `WorkflowRoadmap` showed, but laid
 * out as a vertical roadmap rather than a horizontal flow. Content,
 * images, and step icons all come from the SHARED `workflowStepsData`
 * + `t.home.workflow` dictionary, so titles/descriptions stay
 * localized and edits live in one place.
 *
 * A small rocket is positioned absolutely between the two layers and
 * its position + rotation are driven by scroll progress:
 *   • position  → `path.getPointAtLength(progress * total)`
 *   • rotation  → `Math.atan2(tangentDy, tangentDx)` — symmetric
 *                 tangent (point ahead − point behind) for stability
 *                 at the path's endpoints.
 *
 * Implementation notes:
 *   • Cards reuse the same gradient-border + dark-gray surface +
 *     primary step pill tokens as the original `WorkflowRoadmap`
 *     for visual continuity.
 *   • Rocket math runs inside `requestAnimationFrame`; scroll/resize
 *     listeners are passive and cleaned up on unmount.
 *   • A `ResizeObserver` re-derives the SVG dims AND every card's
 *     measured center on layout shifts (font load, image load,
 *     breakpoint flip, content height changes).
 *   • Honors `prefers-reduced-motion` — when set, the rocket is
 *     parked at the start of the path and no scroll math runs.
 */

// ---------------------------------------------------------------------
// Per-card props derived from `workflowStepsData` + the locale dict.
// Built once per render inside the component so titles/descriptions
// follow the user's chosen language.
// ---------------------------------------------------------------------
type RoadmapStep = {
  id: number;
  step: string;
  title: string;
  description: string;
  image: string;
  icon: IconType;
};

// ---------------------------------------------------------------------
// Layout constants used by both the path generator and the card grid.
// ---------------------------------------------------------------------
// Below this stage width the entire decorative layer (dotted path +
// scroll-driven rocket) is hidden. The snake routing only really
// makes sense once the cards sit in the alternating 3-column
// staircase (lg+); on small and medium screens the cards already
// flow as a clean vertical list, and adding a rocket on top would
// be visually noisy and animate over a viewport that's already short.
// 1024px matches Tailwind's `lg` breakpoint.
const PATH_VISIBLE_BREAKPOINT_PX = 1024;
// Radius for the rounded 90° corners of the Manhattan path. Kept small
// enough that even short segments (in tighter layouts) never overshoot
// — the per-corner clamp below caps it to half the adjacent segment.
const PATH_CORNER_RADIUS = 18;
// Vertical entry point inside each non-first card, expressed as a
// fraction of the card's height (top = 0, bottom = 1). 0.3 lands the
// horizontal segment roughly through the illustration row, matching
// the reference sketch's "enter from the side, then drop down" feel.
const CARD_ENTRY_Y_FRACTION = 0.3;
// Distance (px) by which the path's start and end terminals are tucked
// INSIDE the first / last card. The rocket icon (≈36px) is drawn
// centered on its path point via `translate(-50%, -50%)`, so without
// this inset half the rocket would poke out above card 1 at scroll
// progress 0 and below the last card at scroll progress 1. 28px
// covers half the icon plus a comfortable margin for its drop shadow.
const ROCKET_INSET_PX = 28;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

// ---------------------------------------------------------------------
// Convert a polyline of waypoints into an SVG path string with rounded
// 90° corners. At each interior point we shave off a quadratic-bezier
// arc whose radius is clamped to half the adjacent segment lengths, so
// short segments degrade gracefully instead of producing self-overlap.
// Collinear waypoints (no direction change) emit a degenerate Q that
// renders as a straight line — keeping the algorithm uniform without
// any visual artifact.
// ---------------------------------------------------------------------
type Pt = { x: number; y: number };
const pointsToRoundedPath = (points: Pt[], radius: number): string => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const len1 = Math.hypot(dx1, dy1);
    if (len1 === 0) continue;

    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len2 = Math.hypot(dx2, dy2);
    if (len2 === 0) continue;

    const r = Math.min(radius, len1 / 2, len2 / 2);
    const ax = curr.x - (dx1 / len1) * r;
    const ay = curr.y - (dy1 / len1) * r;
    const bx = curr.x + (dx2 / len2) * r;
    const by = curr.y + (dy2 / len2) * r;

    d += ` L ${ax} ${ay} Q ${curr.x} ${curr.y} ${bx} ${by}`;
  }

  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
};

// ---------------------------------------------------------------------
// Inline rocket — nose points UP (negative Y in SVG coords). Combined
// with the `+ 90deg` adjustment in the tangent rotation below, this
// makes the rocket face along the path's direction of travel.
// ---------------------------------------------------------------------
const RocketIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="drop-shadow-[0_4px_14px_rgba(132,109,255,0.65)]"
  >
    {/* Body */}
    <path
      d="M16 3c3.2 3 5 6.4 5 10v9h-10v-9c0-3.6 1.8-7 5-10z"
      className="fill-white"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {/* Window */}
    <circle
      cx="16"
      cy="12"
      r="2.4"
      className="fill-primary"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    {/* Left fin */}
    <path
      d="M11 17l-3.5 3.5V24h3.5"
      className="fill-primary"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {/* Right fin */}
    <path
      d="M21 17l3.5 3.5V24H21"
      className="fill-primary"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {/* Flame */}
    <path
      d="M13 23.5l1 4.5 1-3 1 3 1-4.5"
      stroke="#fbbf24"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ---------------------------------------------------------------------
// Card — asymmetric "mirrored staircase" layout.
//
// On lg+ the illustration sits on one side and the pill + title
// sit on the OPPOSITE side, alternating per card so every
// illustration leans toward the centre column where the path lives.
// Pulling the title up next to the illustration ensures the column
// opposite the image always carries real content instead of reading
// as an empty corner:
//
//   col 1 cards (even index)      col 3 cards (odd index)
//   ┌─────────────────────────┐   ┌─────────────────────────┐
//   │ [Step]        ┌───────┐ │   │ ┌───────┐        [Step] │
//   │ Title         │ image │ │   │ │ image │         Title │
//   │               └───────┘ │   │ └───────┘               │
//   │ ─                       │   │ ─                       │ ← accent bar
//   │ Description ...         │   │ Description ...         │
//   └─────────────────────────┘   └─────────────────────────┘
//
// Below lg (single-column flow) the alternation collapses entirely
// to a calm centred stack — pill, illustration, accent bar, title,
// description — exactly as before. The same `<h3>` is rendered in
// two slots and toggled per breakpoint with `display: none`, so
// only ONE heading is ever in the accessibility tree at a time.
// A blurred decorative orb sits in the card corner OPPOSITE the
// illustration so depth/glow flips along with the layout, giving
// the staircase a mirrored visual rhythm.
// ---------------------------------------------------------------------
type RoadmapCardProps = RoadmapStep & {
  index: number;
  cardRef: (el: HTMLLIElement | null) => void;
};

const RoadmapCard = ({
  step,
  title,
  description,
  image,
  icon: Icon,
  index,
  cardRef,
}: RoadmapCardProps) => {
  // Grid placement is driven by two CSS variables consumed by the
  // `.lp-card` rule in globals.css (which adds `span 2` and the column
  // assignment under a `min-width: 1024px` media query). With a row of
  // 1, 2, 3, … and the card spanning 2 rows, consecutive cards overlap
  // in adjacent columns — that's the staircase. We use a 3-column grid
  // with cards in cols 1 and 3 (middle col stays empty as a wide visual
  // gap), so even-index cards land at col 1 and odd-index ones at col 3.
  // Below `lg` the grid falls back to its natural single-column flow.
  const placement = {
    "--lp-row": index + 1,
    "--lp-col": index % 2 === 0 ? 1 : 3,
  } as React.CSSProperties;

  // Cards in col 1 (left col, even index) put their illustration on
  // the RIGHT — toward the centre column where the dotted path lives.
  // Cards in col 3 mirror that with the image on the LEFT. The decor
  // orb always sits in the OPPOSITE corner, so depth flips with the
  // layout.
  const isImageRight = index % 2 === 0;

  return (
    // `self-start` keeps each card sized to its own content rather
    // than stretching to fill its 2-row grid track. Without it, the
    // grid would equalize all cards to the tallest one, and the
    // staircase would visually collapse into uniform rectangles.
    <li
      ref={cardRef}
      className="lp-card relative self-start"
      style={placement}
    >
      {/* Gradient-border wrapper — same vocabulary as the rest of the
          home sections, so the new card still feels native to the site. */}
      <div className="relative rounded-2xl bg-gradient-to-br from-primary/40 via-light-border/15 to-transparent p-px">
        {/* Card surface kept ~95% opaque so the dashed path that
            threads behind cards on lg+ doesn't bleed through and
            visually "cross" the illustration. The slight remaining
            translucency + backdrop-blur preserves the glassy depth. */}
        <div className="relative overflow-hidden rounded-[15px] bg-dark-gray/95 backdrop-blur-sm p-5 sm:p-6">
          {/* Decorative blurred orb in the corner OPPOSITE the
              illustration — gives the card a soft asymmetric depth
              glow that flips with the layout, mirroring across the
              staircase. */}
          <div
            aria-hidden
            className={`pointer-events-none absolute -top-16 size-44 rounded-full bg-primary/25 blur-3xl ${
              isImageRight ? "-left-16" : "-right-16"
            }`}
          />

          {/* TOP ZONE — pill column + illustration.
              • lg+: side-by-side flex row, with the pill column on
                the opposite side of the illustration. The pill
                column carries the pill AND the title so the corner
                opposite the image always reads as content rather
                than an empty void. The pill column's cross-axis
                alignment mirrors per card (start for col 1, end
                for col 3) so the cluster always hugs the OUTER
                edge of the card.
              • below lg: stacked vertically — pill on top,
                illustration centred below — for a calm single-
                column flow. The lg-only title is hidden here; a
                second copy of it is rendered inside the body
                block under the illustration. */}
          <div
            className={`relative mb-5 flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:gap-5 ${
              isImageRight ? "" : "lg:flex-row-reverse"
            }`}
          >
            {/* PILL COLUMN — pill at top, lg-only title beneath.
                `lg:flex-1` claims the residual horizontal space so
                the outer-edge alignment lands at the card's outer
                edge regardless of title length; `lg:min-w-0` lets
                the column shrink below its content's intrinsic
                size on tight lg viewports (~1024px) so the title
                wraps to a second line instead of forcing the
                illustration past the card's right edge. */}
            <div
              className={`order-1 flex flex-col items-center gap-3 lg:min-w-0 lg:flex-1 lg:gap-3 ${
                isImageRight ? "lg:items-start" : "lg:items-end"
              }`}
            >
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-text-primary-ligt">
                <Icon className="size-3" />
                <span>{step}</span>
              </span>

              {/* lg-only title — `self-stretch` lets the heading
                  fill the column width so multi-line text wraps
                  inside it; `text-right` mirrors alignment for
                  col-3 cards so the title hugs the card's outer
                  edge alongside the pill. `break-words` is the
                  safety net for unbreakable single-word titles
                  (e.g. "Consultation") at very narrow lg widths.
                  The accessibility-tree duplicate further down
                  uses `lg:hidden`, so only ONE heading is exposed
                  at any breakpoint. */}
              <h3
                className={`hidden lg:block text-lg lg:text-xl font-bold tracking-tight text-text leading-tight break-words lg:self-stretch ${
                  isImageRight ? "" : "lg:text-right"
                }`}
              >
                {title}
              </h3>
            </div>

            {/* Floating illustration with halo + float animation —
                same `wf-orb` / `wf-float` keyframes as the original
                WorkflowRoadmap. The orb uses `inset-4` (not
                `inset-2`) and `bg-primary/35` so its blurred glow
                stays comfortably inside the illustration's 128px
                box — otherwise the blur leaks past the card's
                rounded `overflow-hidden` corner and reads as the
                illustration itself being clipped at the edge.

                Hover zoom: `group` on the outer container scopes
                the hover to the illustration only (no card-wide
                hover effect, per the established design). The
                zoom lives on a SEPARATE inner wrapper from
                `wf-float` so the float's `translateY` and the
                hover's `scale` don't fight over the same
                `transform` property — `wf-float` drives the
                ambient bob, the inner wrapper layers the scale
                on top via composition. */}
            <div className="order-2 group relative w-28 sm:w-32 aspect-square shrink-0">
              <div
                aria-hidden
                className="wf-orb absolute inset-4 rounded-full bg-primary/35 blur-2xl"
                style={{ animationDelay: `${index * 250}ms` }}
              />
              <div
                className="wf-float relative w-full h-full"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="relative h-full w-full transition-transform duration-500 ease-out group-hover:scale-110">
                  <Image
                    src={image}
                    alt={title}
                    fill
                    sizes="(min-width: 1024px) 12vw, 30vw"
                    className="object-contain drop-shadow-[0_10px_30px_rgba(132,109,255,0.35)]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* COPY BLOCK — accent bar + (mobile-only) title +
              description. The mobile title is the screen-reader
              twin of the lg-only title above; exactly one of the
              two is in the accessibility tree at any breakpoint
              thanks to `display: none` toggling. */}
          <div className="relative">
            <div
              aria-hidden
              className="h-0.5 w-10 rounded-full bg-gradient-to-r from-primary to-primary/0 mb-3"
            />
            <h3 className="lg:hidden text-lg font-bold tracking-tight text-text mb-2 leading-tight">
              {title}
            </h3>
            <p className="text-sm text-light-text/85 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------
// Path layer — pure presentational SVG. Receives a pre-computed `d`
// string and forwarded refs so the parent's scroll loop can call
// `getTotalLength()` / `getPointAtLength()` on it.
//
// The stroke is intentionally bumped to 2px @ ~55% primary opacity
// with a 6px-on / 8px-off dash so the line reads as a deliberate
// continuous path on the dark surface, not as random fragments.
// ---------------------------------------------------------------------
type RoadmapPathProps = {
  pathRef: React.RefObject<SVGPathElement | null>;
  svgRef: React.RefObject<SVGSVGElement | null>;
  width: number;
  height: number;
  d: string;
};

const RoadmapPath = ({
  pathRef,
  svgRef,
  width,
  height,
  d,
}: RoadmapPathProps) => {
  if (!d || width === 0 || height === 0) return null;
  return (
    <svg
      ref={svgRef}
      aria-hidden
      // Path lives BEHIND the cards (default z-auto, beneath the
      // card layer at z-10). The dotted line shows in the empty
      // routing area between cards and is intentionally hidden by
      // each card it threads through, treating cards as opaque
      // checkpoints on the route. `pointer-events-none` keeps
      // clicks flowing to anything beneath the SVG.
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray="6 8"
        className="text-primary/55"
      />
    </svg>
  );
};

// ---------------------------------------------------------------------
// Main section.
// ---------------------------------------------------------------------
const RoadmapSection = () => {
  const t = useDict();

  // Build the step list from the SHARED workflow data + the live
  // dictionary so titles/descriptions stay localized and a single edit
  // (e.g. in `roadmap.data.ts` or `locales/*.ts`) updates both this
  // section and any other consumer of the same data.
  const roadmapSteps = useMemo<RoadmapStep[]>(
    () =>
      workflowStepsData.map((s) => ({
        id: s.id,
        step: `${t.home.workflow.stepLabel} ${s.id}`,
        title: t.home.workflow.steps[s.key].title,
        description: t.home.workflow.steps[s.key].description,
        image: s.image,
        icon: s.icon,
      })),
    [t]
  );

  const stageRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const rocketRef = useRef<HTMLDivElement | null>(null);

  // Per-card refs — used so the path generator can anchor to each
  // card's MEASURED vertical center (not just an idealized fraction).
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);

  // Measured pixel size of the stage (the path's coordinate space).
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Per-card bounding boxes in stage-local pixel coords. We need the
  // full box (not just center) because the Manhattan-style path enters
  // and exits cards at their LEFT / RIGHT / TOP / BOTTOM edges. Both
  // axes are measured directly from the DOM so the path tracks the
  // grid wherever it actually places cards (3-col layout, future
  // 2-col layout, mobile single-column, etc.) — no hard-coded column
  // fractions, no assumptions about gap sizes.
  type CardBox = {
    x: number;
    y: number;
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  const [cardBoxes, setCardBoxes] = useState<CardBox[]>([]);

  // True only at viewport widths large enough for the alternating
  // 3-column staircase to make sense (lg+). Driven by `matchMedia`
  // on the VIEWPORT — not the measured stage width — so it aligns
  // exactly with the Tailwind `lg:` breakpoint that drives the grid
  // layout. (Using stage width would under-report at lg because
  // `MaxWidthWrapper` + section padding shrink the stage to a few
  // hundred px below the viewport, leaving the path missing on
  // small lg viewports.)
  const [showPath, setShowPath] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(
      `(min-width: ${PATH_VISIBLE_BREAKPOINT_PX}px)`
    );
    const apply = () => setShowPath(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Reduced-motion preference (re-checked live in case the user toggles
  // it from system settings while the page is open).
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Re-measure stage AND each card on every layout change. ResizeObserver
  // catches reflows that `window.resize` would miss (font / image load,
  // content height changes, breakpoint flips).
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const remeasure = () => {
      const stageRect = stage.getBoundingClientRect();
      const newDims = {
        w: Math.round(stageRect.width),
        h: Math.round(stageRect.height),
      };

      const boxes: CardBox[] = cardRefs.current
        .filter((c): c is HTMLLIElement => c !== null)
        .map((c) => {
          const r = c.getBoundingClientRect();
          const left = r.left - stageRect.left;
          const top = r.top - stageRect.top;
          return {
            x: left + r.width / 2,
            y: top + r.height / 2,
            left,
            right: left + r.width,
            top,
            bottom: top + r.height,
          };
        });

      setDims((prev) =>
        prev.w === newDims.w && prev.h === newDims.h ? prev : newDims
      );
      setCardBoxes((prev) => {
        if (
          prev.length === boxes.length &&
          prev.every(
            (v, i) =>
              v.x === boxes[i].x &&
              v.y === boxes[i].y &&
              v.left === boxes[i].left &&
              v.right === boxes[i].right &&
              v.top === boxes[i].top &&
              v.bottom === boxes[i].bottom
          )
        ) {
          return prev;
        }
        return boxes;
      });
    };

    remeasure();

    const ro = new ResizeObserver(remeasure);
    ro.observe(stage);
    cardRefs.current.forEach((card) => card && ro.observe(card));
    return () => ro.disconnect();
  }, []);

  // Build ONE continuous SVG path string from the current dimensions
  // and measured card boxes.
  //
  //   • mobile  → straight vertical dotted line down the center.
  //   • desktop → MANHATTAN routing with rounded 90° corners. Each
  //               card has an INTERNAL L-shape, and the routing
  //               between cards always happens via the bottom-center
  //               of the previous card. Matches the user's red-line
  //               reference exactly.
  //
  // Desktop traversal model:
  //
  //   ┌──────────┐
  //   │  Card 1  │       Card 1 = vertical pass-through
  //   │    ▼     │       (top of stage → bottom-center).
  //   └────┬─────┘
  //        │
  //        │   ┌──────────────┐
  //        └───▶▶── ─ ─       │       Cards 2..N = INTERNAL L:
  //            │       │      │         enter on the appropriate side
  //            │       ▼      │         at the illustration's row,
  //            │              │         traverse horizontally to the
  //            │              │         card's center, bend DOWN, exit
  //            └──────┬───────┘         at bottom-center.
  //                   │
  //   ┌───────────────┘
  //   │   ┌──────────────┐               Routing between cards always
  //   └───┤── ─ ─◀▶       │               drops straight down out of
  //       │       │      │               the previous card, turns at
  //       │       ▼      │               the next card's entry-Y, and
  //       │              │               travels horizontally to the
  //       └──────┬───────┘               next card's entry edge.
  //              │
  //              ▼
  //
  // After the last card, a final drop carries the rocket to the
  // bottom of the stage so it has somewhere to land at scroll = 1.
  const pathD = useMemo(() => {
    const { w, h } = dims;
    if (w === 0 || h === 0) return "";
    if (cardBoxes.length === 0 || cardBoxes.length !== roadmapSteps.length)
      return "";

    // Skip the path entirely on small / medium viewports — at those
    // breakpoints the cards already render as a simple vertical
    // list, and we don't want a decorative rocket trail there.
    if (!showPath) return "";

    // Entry side helper — a card whose previous neighbour is to its
    // LEFT is entered from its LEFT edge (and vice versa).
    const enteredFromLeft = (i: number) =>
      i > 0 && cardBoxes[i - 1].x < cardBoxes[i].x;

    const points: Pt[] = [];

    // 1) Path origin: start INSIDE card 1, inset by ROCKET_INSET_PX
    //    from its top edge so the rocket icon (centered on the path
    //    point) is fully tucked inside the card at scroll progress 0.
    const card1 = cardBoxes[0];
    points.push({ x: card1.x, y: card1.top + ROCKET_INSET_PX });

    // 2) Card 1 — vertical pass-through. For the multi-card case
    //    this is just an intermediate waypoint on the way down
    //    (collinear with the next routing point); for the single-
    //    card case it becomes the path's terminus and gets the
    //    bottom inset applied.
    if (cardBoxes.length === 1) {
      points.push({ x: card1.x, y: card1.bottom - ROCKET_INSET_PX });
    } else {
      points.push({ x: card1.x, y: card1.bottom });
    }

    // 3) For each subsequent card: route DOWN out of the previous
    //    card's column, ACROSS at the new card's entry-Y, then INTO
    //    the new card on its facing edge, then BEND down inside the
    //    card and EXIT at its bottom-center. The very last card has
    //    its exit Y inset by ROCKET_INSET_PX so the rocket lands
    //    fully inside it at scroll progress 1.
    for (let i = 1; i < cardBoxes.length; i++) {
      const prev = cardBoxes[i - 1];
      const here = cardBoxes[i];
      const isLast = i === cardBoxes.length - 1;

      const entryX = enteredFromLeft(i) ? here.left : here.right;
      const entryY =
        here.top + (here.bottom - here.top) * CARD_ENTRY_Y_FRACTION;

      // Routing: down from prev's bottom-center to entryY (corner),
      // then horizontal to the entry edge of this card.
      points.push({ x: prev.x, y: entryY });
      points.push({ x: entryX, y: entryY });

      // Internal L: horizontal across to the card's center column,
      // then drop straight down to the card's bottom-center (or
      // slightly above it for the terminal card).
      points.push({ x: here.x, y: entryY });
      points.push({
        x: here.x,
        y: isLast ? here.bottom - ROCKET_INSET_PX : here.bottom,
      });
    }

    return pointsToRoundedPath(points, PATH_CORNER_RADIUS);
  }, [dims, cardBoxes, showPath, roadmapSteps.length]);

  // Single-frame rocket update.
  const updateRocket = useCallback(() => {
    const stage = stageRef.current;
    const path = pathRef.current;
    const rocket = rocketRef.current;
    if (!stage || !path || !rocket) return;

    // Scroll progress: linear from "stage top hits viewport mid" → 0
    // to "stage bottom hits viewport mid" → 1. Tied to the stage (not
    // the whole section) so the rocket animates while the user is
    // reading the cards, not the header.
    const rect = stage.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const target = vh / 2;
    const progress = clamp((target - rect.top) / rect.height, 0, 1);

    const totalLen = path.getTotalLength();
    if (totalLen === 0) return;

    // Symmetric tangent (point ahead − point behind). More stable than
    // an ahead-only tangent at the path's endpoints, where lookahead
    // saturates and the direction would otherwise pop.
    const tangentStep = 1.5;
    const len = progress * totalLen;
    const lenAhead = Math.min(totalLen, len + tangentStep);
    const lenBehind = Math.max(0, len - tangentStep);

    const here = path.getPointAtLength(len);
    const ahead = path.getPointAtLength(lenAhead);
    const behind = path.getPointAtLength(lenBehind);

    const dx = ahead.x - behind.x;
    const dy = ahead.y - behind.y;
    const angleRad = Math.atan2(dy, dx);

    // RocketIcon is drawn pointing UP. Tangent angle is from +X axis
    // (atan2 convention), so we add 90° to align "up" with "forward".
    const angleDeg = (angleRad * 180) / Math.PI + 90;

    rocket.style.transform = `translate3d(${here.x}px, ${here.y}px, 0) translate(-50%, -50%) rotate(${angleDeg}deg)`;
  }, []);

  // Scroll / resize → schedule an rAF-throttled rocket update.
  useEffect(() => {
    if (reducedMotion) return;

    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateRocket();
      });
    };

    updateRocket();

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion, updateRocket, dims, pathD]);

  // Park the rocket at the start of the path when reduced motion is on.
  useEffect(() => {
    if (!reducedMotion) return;
    const path = pathRef.current;
    const rocket = rocketRef.current;
    if (!path || !rocket) return;
    const totalLen = path.getTotalLength();
    if (totalLen === 0) return;
    const start = path.getPointAtLength(0);
    rocket.style.transform = `translate3d(${start.x}px, ${start.y}px, 0) translate(-50%, -50%)`;
  }, [reducedMotion, pathD, dims]);

  return (
    <section
      aria-label={t.home.workflow.title}
      className="relative w-full space-y-10 lg:space-y-12"
    >
      {/* Section header — pulls title/description directly from the
          locale dict, so this section reads as the "See How We Work"
          section in whichever language the user picked. Uses the
          shared `SectionHeader` component for typographic rhythm. */}
      <SectionHeader
        title={t.home.workflow.title}
        description={t.home.workflow.description}
      />

      {/* Stage. Spans the full width of its parent container
          (`MaxWidthWrapper`). `pt-20 pb-24` gives the path a tall,
          clearly visible "approach" segment above the first card and
          "departure" segment below the last one — without enough
          padding here, the path's M/L head/tail would render almost
          entirely behind the cards and the rocket would only become
          visible mid-curve. */}
      <div ref={stageRef} className="relative w-full pt-20 pb-24">
        {/* Layer 1 — decorative path (z-0) */}
        <RoadmapPath
          svgRef={svgRef}
          pathRef={pathRef}
          width={dims.w}
          height={dims.h}
          d={pathD}
        />

        {/* Rocket — sits BEHIND the cards alongside the path
            (`z-[5]`, beneath the card layer at z-10). The rocket
            is visible while it travels through the empty routing
            zones between cards and is intentionally occluded each
            time it crosses through a card. Rendered ONLY on lg+
            alongside the path; on smaller viewports the entire
            decorative rocket trail is skipped. */}
        {showPath && (
          <div
            ref={rocketRef}
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 z-[5] text-primary will-change-transform"
            style={{ transform: "translate3d(-9999px, -9999px, 0)" }}
          >
            <RocketIcon />
          </div>
        )}

        {/* Layer 2 — semantic card grid (z-10).
            On md+ this is a 3-column grid: cards live in columns 1
            and 3 (assigned via `--lp-col` from `RoadmapCard`), and
            column 2 stays empty as a wide horizontal gutter — that
            extra horizontal space gives the bezier S-curves a much
            longer, more visible diagonal between consecutive cards.
            The `.lp-card` rule still spans each card across 2 rows
            and starts it one row below the previous, producing the
            alternating staircase. `md:gap-y-64` (256px) is the single
            biggest knob for vertical path visibility: with `span 2`,
            the staircase OVERLAP between consecutive cards is exactly
            one row-track tall, while the VISIBLE bezier between them
            is `(rowTrack + gap)` tall — pushing the gap up keeps row
            tracks short and gives the dotted curve (and the rocket
            riding it) generous open space instead of vanishing into
            the overlap zone. Mobile keeps a clean single-column flow
            with a moderate gap. */}
        <ol
          role="list"
          className="relative z-10 grid grid-cols-1 gap-y-12 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-64"
        >
          {roadmapSteps.map((s, i) => (
            <RoadmapCard
              key={s.id}
              index={i}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
              {...s}
            />
          ))}
        </ol>
      </div>
    </section>
  );
};

export default RoadmapSection;
